import { Button, makeStyles, MessageBar, Text, Tooltip } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import {
    bundleIcon,
    Comment20Filled,
    Comment20Regular,
    Dismiss20Regular,
    TextBulletListSquareSettings20Filled,
    TextBulletListSquareSettings20Regular,
} from '@fluentui/react-icons';
import { EmitType } from '@syncfusion/ej2-base';
import { DataManager } from '@syncfusion/ej2-data';
import { QueryCellInfoEventArgs } from '@syncfusion/ej2-grids';
import {
    ColumnModel,
    ContextMenu,
    ContextMenuItemModel,
    Edit,
    EditEventArgs,
    EditSettingsModel,
    Freeze,
    GridActionEventArgs,
    IIndex,
    Inject,
    LoadingIndicatorModel,
    Resize,
    RowSelectEventArgs,
    SearchSettingsModel,
    VirtualScroll,
} from '@syncfusion/ej2-react-grids';
import { MenuEventArgs } from '@syncfusion/ej2-react-navigations';
import { CSSProperties, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useNavigate } from 'react-router';
import { useIsFeatureEnabled } from '../../../api/features/features';
import {
    useCancelScheduledCalculation,
    useGetTaxReturnById,
    useGetTaxReturnPrintSelectionTOC,
    useSchedulePrintTaxReturn,
    useSetTaxReturnStatus,
} from '../../../api/report/report';
import { ISelectedEntity, resolveColumnForSelectedEntity, resolveSelectedEntityForReport } from '../../../contexts/formViewContext';
import { PrintReportModalProvider } from '../../../contexts/printReportModalContext';
import { useFormView } from '../../../contexts/useFormView';
import { useUserSettings } from '../../../contexts/useUserSettings';
import { ISelectedTaxReturnsAndFilters } from '../../../data-types/exportReports';
import { IFilterItem } from '../../../data-types/filterItem';
import { IContextMenuArgs } from '../../../data-types/IContextMenuArgs';
import { ICurrentCellEventArgs } from '../../../data-types/ICurrentCellEventArgs';
import { ISelectableGrid } from '../../../data-types/ISelectableGrid';
import { TaxReturnTOCItemWithRequiredNodeKey } from '../../../data-types/printReportModalTOCItem';
import { useCommonIndividualReportTableContextMenu } from '../../../hooks/useCommonIndividualReportTableContextMenu';
import { useCommonIndividualReportTableHeaderStyles } from '../../../hooks/useCommonIndividualReportTableHeaderStyles';
import { useEditableTable } from '../../../hooks/useEditableTable';
import { useExportConversations } from '../../../hooks/useExportConversations';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useGridScroll } from '../../../hooks/useGridScroll';
import { useGridState } from '../../../hooks/useGridState';
import { useHighlightSearchedValues } from '../../../hooks/useHighlightSearchedValues';
import { useIsCommentsSubroute } from '../../../hooks/useIsCommentsSubroute';
import { usePreventNavigation } from '../../../hooks/usePreventNavigation';
import { useReportColumnsAsJsx } from '../../../hooks/useReportColumnsAsJsx';
import { useReportCommentsDataSource } from '../../../hooks/useReportCommentsDataSource';
import { useReportCommentsMetadata } from '../../../hooks/useReportCommentsMetadata';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { useSelectedEntities } from '../../../hooks/useSelectedEntities';
import { useTaxReturnCalculationState } from '../../../hooks/useTaxReturnCalculationState';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
    CalculationExecutionStatusCode,
    IWhitePaperColumn,
    IWhitePaperComparedValue,
    IWhitePaperComparedValueDictionary,
    IWhitePaperReport,
    IWhitePaperRow,
    PassthruType,
    ReportStatus,
    ScheduleBatchResponse,
    SetTaxReturnStatusParams,
    TaxReturnTOC,
    TaxReturnTOCKey,
    TOCSelectOption,
    WhitePaperReportExportRequest,
} from '../../../model';
import printReportModalStateReducer, { initialPrintReportModalState } from '../../../reducers/printReportModalStateReducer';
import { customPalette } from '../../../theme';
import { CommandType } from '../../../utils/EventGrid.constants';
import { EntitySelectorInPrintModalEnabled, ExportPrintFormsEnabled } from '../../../utils/FeatureFlags.constants';
import { getEntitiesFilterForColumns } from '../../../utils/FilterUtils';
import {
    formatEditableRowValues,
    getCellHighlightingClasses,
    getCellValueByFieldName,
    getDynamicFieldNameParts,
    isMultiColumnReport,
    isNegativeValue,
    markModifiedValues,
    parseRowValuesToNumber,
    shouldReportBeOnTopOfRecentlyOpened,
    updateEmptyRowTextInGrid,
} from '../../../utils/ReportUtils';
import { ISyncfusionContextMenuItemProps } from '../../../utils/SyncfusionContextMenuUtils';
import {
    clickOnVarianceContainedByCell,
    getClassNameForStyle,
    getFirstFocusableElementInForm,
    getTableKeyWithVisibleFields,
    scrollToTop,
    setFocusOnSyncfusionGridCell,
    shouldUpdateAriaLabel,
} from '../../../utils/TableUtils';
import { areTaxReturnTOCKeysEqual } from '../../../utils/TaxReturnTOCUtils';
import { CALC_REVIEW_COMPARE_HISTORY_HELPID, CALC_REVIEW_OVERVIEW_HELPID, REPORTS_TABLE_URL } from '../../../utils/Urls.constant';
import DefaultSeparator from '../../common/DefaultSeparator';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import ExportReportButton from '../../common/ExportReportButton';
import FunctionalAccessDisableWrapper from '../../common/FunctionalAccessDisableWrapper';
import LoadingSpinner from '../../common/LoadingSpinner';
import PrintReportModal from '../../common/PrintReportModal';
import { ReportStatusButton } from '../../common/ReportStatusButton';
import HelpButton from '../../help/HelpButton';
import { CommentsPanelWrapper } from '../../sidebar/CommentsPanelWrapper';
import { ToggleReportSubscriptionButton } from '../../subscriptions/ToggleReportSubscriptionButton';
import ReportToolbar from '../../toolbar/ReportToolbar';
import { CalculationOptionsPanel } from './CalculationOptionsPanel';
import CommonIndividualReportHeader from './CommonIndividualReportHeader';
import FormViewer from './FormViewer';
import { contextMenuIconStyles, StyledIndividualReportGrid } from './StyledIndividualReportGrid';

const CommentIcon = bundleIcon(Comment20Filled, Comment20Regular);
const CalculationOptionsIcon = bundleIcon(TextBulletListSquareSettings20Filled, TextBulletListSquareSettings20Regular);
export interface ICommonIndividualReportTableContextMenuProps {
    editCell: ISyncfusionContextMenuItemProps<IWhitePaperRow>;
    enterAmounts: ISyncfusionContextMenuItemProps<IWhitePaperRow>;
    drillDown: ISyncfusionContextMenuItemProps<IWhitePaperRow>;
    comments: ISyncfusionContextMenuItemProps<IWhitePaperRow>;
}

export interface ICommonIndividualReportTableProps {
    currentReport?: IWhitePaperReport;
    taxReturnId?: string;
    isError: boolean;
    isTableDataReadyToDisplay: boolean;
    columns: ColumnModel[];
    componentRef?: React.MutableRefObject<ISelectableGrid | null>;
    dataManager?: DataManager;
    currentCellArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null;
    setCurrentCellArgs?: (value: ICurrentCellEventArgs<IWhitePaperRow> | null) => void;
    contextMenuProps?: ICommonIndividualReportTableContextMenuProps;
    reportId: number;
    versionId?: string;
    calculateButton?: React.ReactNode;
    calculationDiagnosticsButton?: React.ReactNode;
    pillar2Button?: React.ReactNode;
    compareButton?: JSX.Element;
    actionBar?: React.ReactNode;
    onRowSelected?: EmitType<RowSelectEventArgs>;
    filterGroupItems: React.ReactNode[];
    clearAllFilters: () => void;
    shouldDisplayCalculationOptions: boolean;
    loadingIndicator?: LoadingIndicatorModel;
    onRenderFinished?: () => void;
    onScrollingStarted?: () => void;
    onGridDataBound?: () => void;
    handleRefetchMetadata?: () => void;
    headerWarningMessage: string | undefined;
    isCollaborationEnabled: boolean;
}

export const descriptionTemplate = (item: IWhitePaperRow) => {
    let classes = '';
    if (item.styles && item.styles.length > 0) {
        classes = item.styles.map((style) => getClassNameForStyle(style)).join(' ');
    }

    return (
        <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='after' content={item.description?.toString()}>
            <span tabIndex={-1} className={classes}>
                {item.description}
            </span>
        </DefaultTooltipHostV9>
    );
};

// As part of migration to Fluent9 all styles should be moved to useStyles hook
const useStyles = makeStyles({
    loadingSpinnerContainer: {
        '--header-height': '300px',
        '--side-panel-width': '315px',
        height: `calc(100vh - var(--header-height))`,
        width: 'calc(100vw - var(--side-panel-width))',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        alignItems: 'center',
        justifyContent: 'center',
    },
    tableGridWrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 215px - var(--message-height, 0px))',
        '@media screen and (max-width: 1300px)': {
            height: 'calc(100vh - 245px - var(--message-height, 0px))',
        },
        margin: '0px 15px',
    },
    scrollablePaneWrapper: {
        // Shrink-wrap to the grid when there are few rows; flex-shrink + maxHeight cap the
        // pane to the remaining viewport so Syncfusion virtualization can scroll internally.
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: 'auto',
        width: '100%',
        height: 'fit-content',
        maxHeight: '100%',
        minHeight: 0,
        background: customPalette.themeLighterAlt,
        border: `1px solid ${customPalette.greyD1D3D4}`,
        borderTop: 'none',
        boxShadow: 'none',
        overflow: 'hidden',
    },
    topContentLeftRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
    },
    topContentRightRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: '5px',
    },
    contextMenuIcons: contextMenuIconStyles,
});

const CommonIndividualReportTable = (props: ICommonIndividualReportTableProps) => {
    const {
        currentReport,
        isError,
        isTableDataReadyToDisplay,
        columns,
        componentRef,
        dataManager,
        currentCellArgs,
        setCurrentCellArgs,
        taxReturnId,
        isCollaborationEnabled,
    } = props;

    const { setShouldBlockNavigation } = usePreventNavigation();
    const { t } = useTranslation();
    const styles = useStyles();
    const { loadingSpinnerContainer, tableGridWrapper, scrollablePaneWrapper, topContentLeftRow, topContentRightRow, contextMenuIcons } =
        styles;
    const {
        showForm,
        tocData: formViewTocData,
        isLoadingToc: isLoadingFormViewToc,
        selectedEntity: formViewSelectedEntity,
        currentTOCItemState,
        clearOverflowPrintRequest,
        pendingOverflowPrintSelection,
    } = useFormView();
    const { showToast } = useToastMessage();

    const [reducerState, updateReducerState] = useReducer(printReportModalStateReducer, initialPrintReportModalState);
    const { mutateAsync: schedulePrintTaxReturn, isLoading: isPrinting } = useSchedulePrintTaxReturn();
    const { mutateAsync: getPrintModalToc } = useGetTaxReturnPrintSelectionTOC();
    const { activeMessage } = useFeedbackMessages();
    const { functionalAccess } = useFunctionalAccess();
    const isPrintModalOpen: boolean = reducerState.isOpen;
    const printModalTocData: TaxReturnTOC | null | undefined = reducerState.tocData;
    const printModalSelectedEntity: IWhitePaperColumn | undefined = reducerState.selectedEntity;
    const isPrintModalTocFetching: boolean = reducerState.isFetchingToc;
    const messageHeight = activeMessage ? '32px' : '0px';
    const tableGridWrapperStyle = { '--message-height': messageHeight } as CSSProperties;

    const { getFilteredEntities } = useSelectedEntities();
    const { applySearch, searchValue, isSearchValueApplied } = useSearchTable();

    const [currentlySelectedCell, setCurrentlySelectedCell] = useState<HTMLElement | undefined>(undefined);
    const { grid: gridRef } = useGridState();
    const isCommentsSubroute = useIsCommentsSubroute();
    //if this is a comments subroute, start with the comments panel open
    const [isCommentsPanelOpen, setIsCommentsPanelOpen] = useState(isCommentsSubroute);
    // Keep reacting to the route after mount too, so navigating (e.g. back/forward, or the
    // viewComments button below) opens/closes the panel the same way a deep link does. This must
    // also close the panel (not just open it) - opening pushes a history entry rather than
    // replacing one, so browser back genuinely lands on a distinct prior entry where this route
    // no longer matches, and nothing else would notice that to close the panel. Refetch the
    // comment metadata on that path too - handleCommentsPanelClose does this for the panel's own
    // close button, but a route-driven close (e.g. browser back) bypasses it, which otherwise
    // leaves stale comment indicators on the grid after a comment is deleted.
    useEffect(() => {
        if (!isCommentsSubroute && isCommentsPanelOpen) {
            props.handleRefetchMetadata?.();
        }
        setIsCommentsPanelOpen(isCommentsSubroute);
    }, [isCommentsSubroute]);
    const [isCalculationOptionsPanelVisible, { setTrue: showCalculationOptionsPanel, setFalse: hideCalculationOptionsPanel }] =
        useBoolean(false);

    const { data: currentReportInformation, error: currentReportInformationError } = useGetTaxReturnById(props.reportId);

    const entities: IFilterItem[] = getEntitiesFilterForColumns(currentReport?.columns || []);
    const filteredEntities: IFilterItem[] = getFilteredEntities(entities);

    //Use custom hook to separate the JSX element creation for columns
    const { columnsAsJsx, columnsToSearch } = useReportColumnsAsJsx(columns, filteredEntities);
    const { focusCellAfterEventCompleted, formatFieldValues, highlightCell } = useEditableTable<IWhitePaperRow>({
        parseRowValuesToNumber: (row) => parseRowValuesToNumber(row!),
        getCellHighlightingClasses,
        formatEditableRowValues,
        markModifiedValues,
        formElementToFocusOnEditBegin: (args: EditEventArgs) => getFirstFocusableElementInForm(args!.form!),
    });
    const { mostRecentlyOpenedTabs, updateReturnTabs } = useUserSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const commentsFormsMatch = useMatch('/view/:reportId/forms/:tocItemId/comments/:commentId?');
    const commentsBareMatch = useMatch('/view/:reportId/comments/:commentId?');
    const {
        currentRowIndex,
        currentColumnIndex,
        shouldScrollToRowOnDataBound,
        shouldScrollToColumnOnDataBound,
        setShouldScrollOnDataBoundFalse,
        setShouldScrollToColumnOnDataBoundFalse,
    } = useGridScroll();
    const { isReportExecuting, updateTaxReturnCalculationReport, getReportStatus, updateTaxReturnProcessingStatus } =
        useTaxReturnCalculationState();
    const reportStatus = getReportStatus(props.reportId);

    const { mutateAsync: cancelScheduledCalculation } = useCancelScheduledCalculation({
        mutation: {
            onSuccess: (wasCanceled: any) => {
                if (wasCanceled) {
                    updateTaxReturnCalculationReport({
                        taxReturnItemName: currentReport?.name,
                        taxReturnItemKey: props.reportId,
                        jobId: reportStatus?.jobId ?? 0,
                        statusCode: CalculationExecutionStatusCode.Canceled,
                        commandType: CommandType.Execute,
                    });

                    if (!currentReportInformation?.executed) {
                        navigate(REPORTS_TABLE_URL);
                    }
                }
            },
        },
    });

    const { menuItemOptions: menuItems, updateMenuItemsUsability } = useCommonIndividualReportTableContextMenu(props.contextMenuProps);

    const contextMenuItemClassName = contextMenuIcons;
    const contextMenuItems: Array<ContextMenuItemModel> = menuItems.map((menuItem) => menuItem.contextMenuItem);

    const { storeSearchValueToHighlight, highlightSearchValueOnRender } = useHighlightSearchedValues(getCellValueByFieldName);
    const { data: isExportPrintFormsEnabled } = useIsFeatureEnabled({
        featureFlagName: ExportPrintFormsEnabled,
    });
    //const isEntitySelectorEnabled: boolean = true;
    const { data: isEntitySelectorEnabled } = useIsFeatureEnabled({ featureFlagName: EntitySelectorInPrintModalEnabled });

    const tableKey = getTableKeyWithVisibleFields(columnsToSearch, props.versionId ?? '', columns);
    const isLargeData = useMemo(() => columns.length > 25, [columns.length]);
    const hasReportOptionsAccess = functionalAccess?.reportOptionsButtonEnabled;
    const doNotPrintBlankForms: boolean = reducerState.doNotPrintBlankForms;

    useEffect(() => {
        if (gridRef && gridRef.current && searchValue !== '') {
            applySearch(gridRef);
        }
    }, [dataManager]);

    useEffect(() => {
        const isInTaxReturnCalculationStatusList = isReportExecuting(props.reportId);
        if (props.reportId && isTableDataReadyToDisplay && isInTaxReturnCalculationStatusList) {
            updateTaxReturnProcessingStatus(props.reportId);
        }
    }, [props.reportId, isTableDataReadyToDisplay]);

    useEffect(() => {
        if (shouldReportBeOnTopOfRecentlyOpened(currentReportInformation, mostRecentlyOpenedTabs)) {
            updateReturnTabs(
                {
                    returnKey: Number(currentReportInformation?.id),
                    returnName: currentReportInformation?.name ?? '',
                    statusCode:
                        mostRecentlyOpenedTabs?.filter((item) => item.returnKey === Number(currentReportInformation?.id))[0]?.statusCode ||
                        CalculationExecutionStatusCode.Unknown,
                },
                true
            );
        }
    }, [currentReportInformation]);

    const handleContextMenuItemClick = (args: MenuEventArgs) => {
        // iterate over the collection of menu items and call its click handler.
        // if it returns true, break out of the loop, else keep going
        for (const menuItem of menuItems) {
            // to avoid magic strings, each click handler checks that the args id matches and return false if not
            // so we invoke each in turn, because god forbid syncfusion has click handlers for each item, no, it's for the entire context menu like animals
            if (menuItem!.handleContextMenuItemClick(args, currentCellArgs)) {
                break;
            }
        }
    };

    if (componentRef) {
        componentRef.current = {
            focusCell: (_: IIndex) => {
                setFocusOnSyncfusionGridCell(currentlySelectedCell);
            },
            startEdit: (index: IIndex) => {
                const clickedRow = gridRef.current
                    ?.getRows()
                    .find((row) => row.getAttribute('data-rowindex') === index.rowIndex?.toString());

                if (clickedRow) {
                    gridRef.current?.editModule.startEdit(clickedRow as HTMLTableRowElement);
                }
            },
            markAsEdited: (index: IIndex) => {
                const gridDataManager: DataManager = gridRef.current!.dataSource as DataManager;
                const dataManagerJsonArray: Object[] = gridDataManager.dataSource.json!;
                const row = dataManagerJsonArray[index.rowIndex!] as IWhitePaperRow;
                const values: IWhitePaperComparedValueDictionary = row.values!;
                const cellIndex: number = index.cellIndex!;
                const { entityCode } = getDynamicFieldNameParts(columns[cellIndex].field!);
                const comparedValue: IWhitePaperComparedValue = values[entityCode]!;
                comparedValue.isChangedByDrilldown = true;
                // refreshes the entire grid, which might be overkill, but it triggers the cell highlighting callback we define in the parent component
                gridRef.current!.refresh();
            },
            refreshGrid: () => {
                if (gridRef.current) {
                    gridRef.current.refresh();
                }
            },
        };
    }

    const getClickedTdElement = (target: HTMLElement): HTMLElement => {
        if (target.tagName !== 'td') {
            return target.closest('td') as HTMLElement;
        }

        return target;
    };

    const handleContextMenuOpen = (args: IContextMenuArgs<IWhitePaperRow>) => {
        // we can't use args.rowInfo.cellIndex when column virtualization is enabled.
        // when it is enabled, cellIndex is the index of the column within the virtualized
        // set. So if a given column is the 40th column, but only columns 30-40 are visible,
        // then args.rowInfo.cellIndex will be 10, not 40.
        const cellIndex: IIndex = {
            cellIndex: args.column.index,
            // this value seems to be consistent even if row virtualization is enabled
            rowIndex: args.rowInfo.rowIndex,
        };

        if (gridRef && gridRef.current) {
            if (gridRef.current.isEdit) {
                args.cancel = true;
                return;
            }

            gridRef.current.contextMenuModule.contextMenu.onClose = () => {
                setFocusOnSyncfusionGridCell(args.rowInfo.cell);
            };
            updateMenuItemsUsability(args, gridRef.current);
        }

        if (setCurrentCellArgs && args.column) {
            setCurrentCellArgs({
                cellIndex: cellIndex,
                data: args.rowInfo.rowData as IWhitePaperRow,
                fieldName: args.column.field,
            });
        }

        setTimeout(() => {
            const clickedElement = getClickedTdElement(args.event.target as HTMLElement);
            if (clickedElement) {
                setCurrentlySelectedCell(clickedElement);
                clickedElement.classList.add('e-focused');
            }
        }, 400);
    };

    const handleCreated = () => {
        if (props.onRenderFinished) {
            props.onRenderFinished();
        }
        if (gridRef.current) {
            gridRef.current.contextMenuModule.contextMenu.cssClass += ` ${contextMenuItemClassName}`;
            setShouldBlockNavigation(false);
        }
    };

    const updateAriaLabelAndCustomizeCell = (args: QueryCellInfoEventArgs) => {
        highlightSearchValueOnRender(args);

        if (shouldUpdateAriaLabel(args)) {
            const cellValue = getCellValueByFieldName(args.data as IWhitePaperRow, args.column?.field ?? '');
            const isNegative = isNegativeValue(cellValue?.toString());

            if (args.cell && cellValue) {
                args.cell?.setAttribute('aria-label', `${isNegative ? t('minus') : ''}${cellValue}`);
            }
        }

        if (isCollaborationEnabled && args.cell) {
            const fieldName = args.column?.field?.split('.')[1]; // it looks like this values.FD1010P_10.current
            const totalUnresolvedComments = (args.data as IWhitePaperRow)?.values?.[fieldName || '']?.totalUnresolvedComments;
            const totalComments = (args.data as IWhitePaperRow)?.values?.[fieldName || '']?.totalComments;

            // if we already set the aria label, append to that, otherwise grab the field value and append to THAT
            let currentLabel = args.cell?.getAttribute('aria-label');
            if (!currentLabel) {
                currentLabel = getCellValueByFieldName(args.data as IWhitePaperRow, args.column?.field ?? '') ?? '';
            }

            args.cell.classList.remove('hasUnresolvedComments');
            args.cell.classList.remove('hasOnlyResolvedComments');
            if ((totalUnresolvedComments ?? 0) > 0) {
                args.cell.classList.add('hasUnresolvedComments');
                args.cell?.setAttribute('aria-label', `${currentLabel} ${t('hasUnresolvedComments')}`);
            } else if ((totalComments ?? 0) > 0) {
                args.cell.classList.add('hasOnlyResolvedComments');
                args.cell?.setAttribute('aria-label', `${currentLabel} ${t('hasResolvedComments')}`);
            }
        }

        highlightCell(args);
    };

    const onDatabound = () => {
        if (shouldScrollToRowOnDataBound) {
            setShouldScrollOnDataBoundFalse();
            setTimeout(() => {
                if (gridRef.current) {
                    if (currentRowIndex !== null && currentRowIndex.current !== null) {
                        gridRef.current.selectRow(currentRowIndex.current);
                    }
                    currentRowIndex.current = null;
                }
            }, 250);
        }

        if (shouldScrollToColumnOnDataBound) {
            setShouldScrollToColumnOnDataBoundFalse();
            setTimeout(() => {
                if (gridRef.current) {
                    if (currentColumnIndex !== null && currentColumnIndex.current !== null) {
                        clickOnVarianceContainedByCell(gridRef.current, {
                            rowIndex: currentRowIndex.current ?? undefined,
                            cellIndex: currentColumnIndex.current ?? undefined,
                        });

                        currentColumnIndex.current = null;
                    }
                }
            }, 250);
        }

        if (gridRef && gridRef.current) {
            updateEmptyRowTextInGrid(gridRef.current, t('noRowsMeetTheCriteria'));
        }

        if (props.onGridDataBound) {
            props.onGridDataBound();
        }
    };

    const searchSettings: SearchSettingsModel = {
        fields: columnsToSearch,
        ignoreCase: true,
    };

    const editSettings: EditSettingsModel = {
        allowEditing: true,
        allowAdding: false,
        allowDeleting: false,
        mode: 'Normal',
        allowEditOnDblClick: false,
    };

    const handleActionComplete = (args: GridActionEventArgs) => {
        focusCellAfterEventCompleted(args);

        if (args.requestType === 'searching') {
            scrollToTop(gridRef.current);
        }
    };

    const handleActionBegin = (args: GridActionEventArgs) => {
        if (props.onScrollingStarted && args.requestType === 'virtualscroll') {
            props.onScrollingStarted();
        }
        formatFieldValues(args);
        storeSearchValueToHighlight(args);
    };

    const handleCancelCalculation = () => {
        if (reportStatus && reportStatus.jobId) cancelScheduledCalculation({ data: { jobId: reportStatus.jobId } });
    };

    const { mutateAsync: setTaxReturnStatus } = useSetTaxReturnStatus({
        mutation: {
            onSuccess: (data: any, { params }: { params: SetTaxReturnStatusParams }) => {
                if (data) {
                    if (currentReportInformation) {
                        currentReportInformation.status = params?.reportStatus;
                    }
                }
            },
        },
    });

    const handleStatusChange: (key: ReportStatus) => void = async (key: ReportStatus) => {
        try {
            //todo: processing indicator
            setTaxReturnStatus({ reportId: props.reportId, params: { reportStatus: key } });
            //todo: error indicator
        } catch (error: any) {
            if (error) {
                throw new Error(error?.request?.response);
            }
        }
    };
    const { currentReportNameTextStyles, statusText } = useCommonIndividualReportTableHeaderStyles();

    const handleCommentsPanelClose = () => {
        if (props.handleRefetchMetadata) {
            props.handleRefetchMetadata();
        }
        setIsCommentsPanelOpen(false);

        // The panel was opened by navigating to a /comments URL (see the viewComments button
        // below) - closing it via the panel's own close button doesn't navigate anywhere on its
        // own, so without this the address bar (and a later browser-back press) would still point
        // at the comments subroute with nothing showing. replace: true so closing doesn't add its
        // own back-button stop.
        if (commentsFormsMatch) {
            navigate(
                {
                    pathname: `/view/${commentsFormsMatch.params.reportId}/forms/${commentsFormsMatch.params.tocItemId}`,
                    search: location.search,
                },
                { replace: true }
            );
        } else if (commentsBareMatch) {
            navigate({ pathname: `/view/${commentsBareMatch.params.reportId}`, search: location.search }, { replace: true });
        }
    };

    const getReportName = () => {
        const reportToExport = getCurrentReportAsArray()?.selectedTaxReturns?.[0];
        return reportToExport?.name || '';
    };

    const currentReportIsCalculating = isReportExecuting(props.reportId) || false;
    const shouldDisplayIsLoadingSpinner = currentReportIsCalculating || !isTableDataReadyToDisplay || isPrinting;
    const hasUnresolvableReportError = currentReportInformationError?.response?.status === 404 || isError;
    const shouldDisableInteractiveElements = shouldDisplayIsLoadingSpinner || hasUnresolvableReportError;

    const getPrintModalSelectedEntity = (column: IWhitePaperColumn | undefined): ISelectedEntity | undefined => {
        const columnId: number | undefined = column?.columnId;
        const columnIndex: number | undefined = column?.originalColumnIndex ?? column?.columnIndex;

        if (!Number.isInteger(columnId) || !Number.isInteger(columnIndex)) {
            return undefined;
        }

        return {
            id: columnId ?? 0,
            index: columnIndex ?? 0,
            code: column?.entityCode ?? '',
        };
    };

    const getDefaultPrintModalEntity = (fromFormView: boolean): IWhitePaperColumn | undefined => {
        const reportColumns: IWhitePaperColumn[] = currentReport?.columns ?? [];
        if (reportColumns.length === 0) {
            return undefined;
        }

        if (isEntitySelectorEnabled !== true) {
            return reportColumns[0];
        }

        if (!fromFormView) {
            return reportColumns[0];
        }

        const resolvedEntity: ISelectedEntity | undefined =
            formViewSelectedEntity ??
            currentTOCItemState?.entity ??
            resolveSelectedEntityForReport(new URLSearchParams(location.search), reportColumns);

        return resolveColumnForSelectedEntity(resolvedEntity, reportColumns) ?? reportColumns[0];
    };

    const isEffectivePrintModalOpen: boolean = isPrintModalOpen;

    const printModalSelectedEntityData: ISelectedEntity | undefined = useMemo(() => {
        const columnId: number | undefined = printModalSelectedEntity?.columnId;
        const columnIndex: number | undefined = printModalSelectedEntity?.originalColumnIndex ?? printModalSelectedEntity?.columnIndex;

        if (!Number.isInteger(columnId) || !Number.isInteger(columnIndex)) {
            return undefined;
        }

        return {
            id: columnId ?? 0,
            index: columnIndex ?? 0,
            code: printModalSelectedEntity?.entityCode ?? '',
        };
    }, [printModalSelectedEntity]);

    const isMultiColumnNonPartnership: boolean =
        isMultiColumnReport(currentReport) && printModalTocData?.returnType !== PassthruType.Partnership;
    const isTotalEntity: boolean =
        !!printModalSelectedEntity &&
        !!currentReport?.columns?.[0] &&
        printModalSelectedEntity.columnId === currentReport.columns[0].columnId;
    const shouldIncludeAllColumns: boolean = isEntitySelectorEnabled === true;
    const shouldSetAllColumns: boolean = isEntitySelectorEnabled === true && isMultiColumnNonPartnership && isTotalEntity;

    // ideally we would not use an effect for this.
    // but refactoring away from effects is a larger effort that will touch a lot of areas, so
    // we're going to wait
    useEffect(() => {
        if (pendingOverflowPrintSelection === undefined) {
            return;
        }
        openPrintModal(pendingOverflowPrintSelection);
    }, [pendingOverflowPrintSelection]);

    const openPrintModal = (
        initialSelectedTocItems?: TaxReturnTOCItemWithRequiredNodeKey[],
        fromFormView: boolean = initialSelectedTocItems !== undefined || showForm
    ): void => {
        const defaultEntity: IWhitePaperColumn | undefined = getDefaultPrintModalEntity(fromFormView);
        const hasInitialSelectedTocItems: boolean = initialSelectedTocItems !== undefined;

        if (hasInitialSelectedTocItems && isEntitySelectorEnabled === true) {
            updateReducerState({
                type: 'modal-opened-overflow',
                selectedEntity: defaultEntity,
                tocData: formViewTocData,
                initialSelectedTocItems: initialSelectedTocItems,
            });
        } else {
            updateReducerState({ type: 'modal-opened', selectedEntity: defaultEntity });

            if (defaultEntity) {
                void fetchPrintModalTocData(defaultEntity, initialSelectedTocItems);
            }
        }
    };

    const closePrintModal = (): void => {
        updateReducerState({ type: 'modal-closed' });
        clearOverflowPrintRequest();
    };

    const fetchPrintModalTocData = async (
        entityColumn: IWhitePaperColumn | undefined,
        initialSelectedTocItemsOverride?: TaxReturnTOCItemWithRequiredNodeKey[]
    ): Promise<void> => {
        const selectedEntity: ISelectedEntity | undefined = getPrintModalSelectedEntity(entityColumn);

        if (!selectedEntity) {
            return;
        }

        updateReducerState({ type: 'toc-fetch-start' });
        try {
            const fetchedTocData: TaxReturnTOC = await getPrintModalToc({
                data: {
                    taxReturnItemTOCKey: props.reportId,
                    columnIndex: selectedEntity.index,
                    entityId: selectedEntity.id,
                    entityCode: selectedEntity.code,
                },
            });
            updateReducerState({
                type: 'toc-fetch-success',
                tocData: fetchedTocData,
                initialSelectedTocItems: initialSelectedTocItemsOverride,
            });
        } catch (error: unknown) {
            console.error('Error loading print modal TOC', error);
            updateReducerState({ type: 'toc-fetch-failure' });
        } finally {
            updateReducerState({ type: 'toc-fetch-finished' });
        }
    };

    const getCurrentReportAsArray = (): ISelectedTaxReturnsAndFilters => {
        const request: WhitePaperReportExportRequest = currentReportInformation as WhitePaperReportExportRequest;
        if (currentReportInformation) {
            if (props.versionId) {
                request.isCompare = true;
                request.versionId = props.versionId;
            }
            const selectedTaxReturnsFilter: ISelectedTaxReturnsAndFilters = { selectedTaxReturns: [request] };
            return selectedTaxReturnsFilter;
        }

        return {};
    };

    const handlePrintModalSubmit = async (
        tocSelectOptions: TOCSelectOption[] | undefined,
        doNotPrintBlankForms: boolean
    ): Promise<void> => {
        try {
            const selectedTocOptions: TOCSelectOption[] = tocSelectOptions ?? [];
            const reportsToExport: WhitePaperReportExportRequest[] | undefined = getCurrentReportAsArray().selectedTaxReturns;

            const scheduleBatchResponse: ScheduleBatchResponse = await schedulePrintTaxReturn({
                data: {
                    returnId: taxReturnId,
                    taxReturnItemTOCKey: reportsToExport?.[0].id,
                    tocSelectOptions: selectedTocOptions,
                    columnIndex: printModalTocData?.returnType !== PassthruType.Partnership ? printModalSelectedEntityData?.index : 0, //when printing on partnership reports, we should always pass column index of 0
                    doNotPrintBlankForms: doNotPrintBlankForms,
                },
            });

            const reportToExport: WhitePaperReportExportRequest | undefined = reportsToExport?.[0];
            const pendingOverflowTocKey: TaxReturnTOCKey | null | undefined = pendingOverflowPrintSelection?.[0]?.taxReturnTOCKey;
            const shouldPassPendingOverflowTocItemKey: boolean = selectedTocOptions.some((option: TOCSelectOption): boolean => {
                const isMatchingTocKey: boolean = areTaxReturnTOCKeysEqual(option.tocKey, pendingOverflowTocKey);

                return option.print === true && isMatchingTocKey;
            });

            if (reportToExport) {
                updateTaxReturnCalculationReport({
                    taxReturnItemKey: reportToExport.id,
                    taxReturnItemName: reportToExport.name,
                    commandType: CommandType.Print,
                    statusCode: CalculationExecutionStatusCode.Executing,
                    batchProcessId: scheduleBatchResponse.batchProcessId,
                    jobId: scheduleBatchResponse.jobKey,
                    ...(pendingOverflowTocKey && shouldPassPendingOverflowTocItemKey ? { tocItemKey: pendingOverflowTocKey } : {}),
                });
            }

            closePrintModal();
            showToast(t('preparingReturnFile'), <span>{t('preparingReturnFileDescription')}</span>, 'info');
        } catch (error: unknown) {
            console.error('Error scheduling print', error);
        }
    };

    // Create data source and metadata for the second comments panel using new hooks
    const reportDataSource = useReportCommentsDataSource(props.reportId.toString(), currentReportInformation?.name ?? undefined);
    const reportMetadata = useReportCommentsMetadata(props.reportId.toString(), currentReportInformation?.name ?? undefined);
    const reportExportConversations = useExportConversations(true, getCurrentReportAsArray);

    const topContentLeft: React.ReactNode[] = [
        <div key='topContentLeft'>
            <div className={topContentLeftRow}>
                <h2 className={currentReportNameTextStyles}>{currentReportInformation?.name}</h2>
                <Text className={statusText}>{t('status').toString()}:</Text>
                <div>
                    <ReportStatusButton
                        status={currentReportInformation?.status ?? ReportStatus.NotStarted}
                        onChange={handleStatusChange}
                        disabled={shouldDisableInteractiveElements}
                    />
                </div>
            </div>
        </div>,
    ];

    const calculationOptionsButton = (
        <Tooltip
            content={t('calculationOptions').toString()}
            relationship='description'
            appearance='inverted'
            withArrow
            positioning='below'
        >
            <Button
                id={t('calculationOptions').toString()}
                appearance='subtle'
                icon={<CalculationOptionsIcon />}
                aria-label={t('calculationOptions').toString()}
                onClick={showCalculationOptionsPanel}
                disabled={hasReportOptionsAccess === false || shouldDisableInteractiveElements}
            />
        </Tooltip>
    );

    const onPrintEntitySelect = (entity: IWhitePaperColumn | undefined) => {
        const currentColumnId: number | undefined = printModalSelectedEntity?.columnId;
        const nextColumnId: number | undefined = entity?.columnId;
        const hasEntityChanged: boolean = currentColumnId !== nextColumnId;

        if (!hasEntityChanged) {
            return;
        }

        updateReducerState({ type: 'entity-state-changed', selectedEntity: entity });

        if (!entity) {
            return;
        }

        void fetchPrintModalTocData(entity);
    };

    const topContentRight: React.ReactNode[] = [
        <div className={topContentRightRow} key='topContentRight'>
            <ToggleReportSubscriptionButton reportId={props.reportId} disabled={shouldDisableInteractiveElements} />
            {isCollaborationEnabled && (
                <>
                    <div>
                        <CommentsPanelWrapper
                            dataSource={reportDataSource}
                            metadata={reportMetadata}
                            exportConversations={reportExportConversations}
                            isOpen={isCommentsPanelOpen}
                            onClose={handleCommentsPanelClose}
                            isCollaborationEnabled={isCollaborationEnabled}
                        />
                    </div>
                    <Tooltip
                        content={t('viewComments').toString()}
                        relationship='description'
                        positioning='below'
                        appearance='inverted'
                        withArrow
                    >
                        <Button
                            appearance='subtle'
                            icon={<CommentIcon />}
                            aria-label={t('viewComments').toString()}
                            onClick={() => {
                                // Navigate rather than opening the panel via local state (matching
                                // the per-cell comments flow) so the address bar reflects the open
                                // panel and it can be deep-linked/refreshed. Keep the /forms/:tocItemId
                                // prefix when on the forms tab so the form stays displayed underneath.
                                // This component is also reused by the compare-versions view, which has
                                // no /compare/.../comments route - only navigate when we're actually on
                                // a /view/... route, otherwise fall back to the pre-existing local-state
                                // open so compare's own panel keeps working unchanged.
                                if (location.pathname.startsWith(`/view/${props.reportId}`)) {
                                    const formSegment =
                                        showForm && currentTOCItemState?.id ? `/forms/${encodeURIComponent(currentTOCItemState.id)}` : '';
                                    navigate({ pathname: `/view/${props.reportId}${formSegment}/comments`, search: location.search });
                                } else {
                                    setIsCommentsPanelOpen(true);
                                }
                            }}
                            disabled={shouldDisableInteractiveElements}
                        />
                    </Tooltip>
                </>
            )}
            {props.shouldDisplayCalculationOptions ? (
                <>
                    {hasReportOptionsAccess ? (
                        calculationOptionsButton
                    ) : (
                        <FunctionalAccessDisableWrapper hasFunctionalAccess={hasReportOptionsAccess}>
                            {calculationOptionsButton}
                        </FunctionalAccessDisableWrapper>
                    )}
                </>
            ) : null}
            {isCollaborationEnabled || props.shouldDisplayCalculationOptions ? <DefaultSeparator /> : null}
            {props.calculateButton}
            {props.calculationDiagnosticsButton}
            <ExportReportButton
                label={t('exportPrint').toString()}
                getReportsToExport={getCurrentReportAsArray}
                disabled={shouldDisableInteractiveElements}
                onPrintRequest={isExportPrintFormsEnabled ? () => openPrintModal(undefined, showForm) : undefined}
            />
            {props.pillar2Button}

            {isEffectivePrintModalOpen && (
                <PrintReportModalProvider
                    uiState={reducerState}
                    formTOCData={printModalTocData}
                    isLoadingFormTOC={isPrintModalTocFetching || isLoadingFormViewToc}
                    entities={currentReport!.columns!}
                    selectedEntity={printModalSelectedEntity!}
                    dispatch={updateReducerState}
                    shouldIncludeAllColumns={shouldIncludeAllColumns}
                    shouldSetAllColumns={shouldSetAllColumns}
                    isTotalEntitySelected={isTotalEntity}
                    onEntitySelect={onPrintEntitySelect}
                    isEntitySelectorEnabled={isEntitySelectorEnabled === true && entities.length > 1}
                    isDoNotPrintBlankFormsAvailable={isEntitySelectorEnabled ?? false}
                    doNotPrintBlankForms={doNotPrintBlankForms}
                >
                    <PrintReportModal
                        reportName={getReportName()}
                        isOpen={isEffectivePrintModalOpen}
                        onClose={closePrintModal}
                        onSubmit={handlePrintModalSubmit}
                        isSubmitting={isPrinting}
                        entityDescription={
                            currentReportInformation?.entityCode
                                ? `${currentReportInformation.entityCode} - ${currentReportInformation.entityName}`
                                : undefined
                        }
                    />
                </PrintReportModalProvider>
            )}

            <HelpButton helpId={props.versionId ? CALC_REVIEW_COMPARE_HISTORY_HELPID : CALC_REVIEW_OVERVIEW_HELPID} />
        </div>,
    ];

    const isFiltered = isSearchValueApplied || filteredEntities.length > 0;

    return (
        <div data-testid='commonIndividualReportTable'>
            <CommonIndividualReportHeader
                topContentLeft={topContentLeft}
                topContentRight={topContentRight}
                actionBar={props.actionBar}
                warningMessage={props.headerWarningMessage}
            />
            {!showForm && currentReportInformationError?.response?.status === 404 ? (
                <MessageBar intent='error'>{t('reportNotFoundError')}</MessageBar>
            ) : !showForm && isError ? (
                <label>{t('error')}</label>
            ) : (
                <>
                    {!showForm && shouldDisplayIsLoadingSpinner && (
                        <div className={loadingSpinnerContainer}>
                            <LoadingSpinner
                                id='loadingSpinner'
                                label={currentReportIsCalculating ? t('calculating').toString() : t('loadingPleaseWait').toString()}
                                labelPosition={currentReportIsCalculating ? 'below' : 'before'}
                            />
                            {currentReportIsCalculating && (
                                <Button
                                    appearance='secondary'
                                    aria-label={t('stopRunning').toString()}
                                    aria-disabled={!reportStatus?.jobId}
                                    disabled={!reportStatus?.jobId}
                                    onClick={handleCancelCalculation}
                                    icon={<Dismiss20Regular />}
                                >
                                    {t('stopRunning').toString()}
                                </Button>
                            )}
                        </div>
                    )}
                    <div className={tableGridWrapper} style={tableGridWrapperStyle}>
                        <FormViewer
                            returnKey={currentReportInformation?.id}
                            reportColumns={currentReport?.columns ?? []}
                            isTableDataReadyToDisplay={isTableDataReadyToDisplay}
                            onPrintRequest={(initialSelectedTocItems) => openPrintModal(initialSelectedTocItems, true)}
                        />

                        {!showForm && !shouldDisplayIsLoadingSpinner ? (
                            <>
                                <ReportToolbar
                                    filterGroupItems={props.filterGroupItems}
                                    isFiltered={isFiltered}
                                    clearAllFilters={props.clearAllFilters}
                                    compareButton={props.compareButton}
                                />
                                <div className={scrollablePaneWrapper}>
                                    <StyledIndividualReportGrid
                                        aria-label={'Report Details'}
                                        dataSource={dataManager}
                                        allowResizing={true}
                                        ref={gridRef}
                                        enableVirtualization={true}
                                        enableColumnVirtualization={isLargeData}
                                        enableVirtualMaskRow={true}
                                        pageSettings={{ pageSize: 40 }}
                                        rowHeight={36}
                                        contextMenuItems={contextMenuItems}
                                        contextMenuClick={handleContextMenuItemClick}
                                        contextMenuOpen={handleContextMenuOpen}
                                        queryCellInfo={updateAriaLabelAndCustomizeCell}
                                        created={handleCreated}
                                        allowTextWrap={false}
                                        dataBound={onDatabound}
                                        editSettings={editSettings}
                                        actionComplete={handleActionComplete}
                                        actionBegin={handleActionBegin}
                                        searchSettings={searchSettings}
                                        key={tableKey}
                                        rowSelected={props.onRowSelected}
                                        loadingIndicator={props.loadingIndicator}
                                    >
                                        {columnsAsJsx}
                                        <Inject services={[Resize, VirtualScroll, Freeze, ContextMenu, Edit]} />
                                    </StyledIndividualReportGrid>
                                </div>
                            </>
                        ) : null}
                    </div>
                    {isCalculationOptionsPanelVisible && (
                        <CalculationOptionsPanel
                            calculationOptions={currentReport?.options || []}
                            entityCode={currentReportInformation?.entityCode || ''}
                            isOpen={isCalculationOptionsPanelVisible}
                            columns={currentReport?.columns || []}
                            onClose={hideCalculationOptionsPanel}
                            jurisdiction={currentReportInformation?.jurisdiction || undefined}
                            period={currentReportInformation?.period || undefined}
                            case={currentReportInformation?.case || undefined}
                            taxReturnKey={currentReportInformation?.id}
                            year={currentReportInformation?.year}
                            reportName={currentReportInformation?.name}
                            taxReturnItemId={currentReportInformation?.taxReturnItemId}
                        ></CalculationOptionsPanel>
                    )}
                </>
            )}
        </div>
    );
};

export default CommonIndividualReportTable;
